/*
 * This code is protected by intellectual property rights.
 * Dr. Ing. h.c. F. Porsche AG owns exclusive rights of use.
 * © 2017-2026, Dr. Ing. h.c. F. Porsche AG.
 */

import { inject, Injectable } from '@angular/core'
import { HttpClient, HttpHeaders } from '@angular/common/http'
import { Observable } from 'rxjs'
import { MyprofileProfileCompletionDetails } from '../../../../../../libs/myprofile-profile-completion-status-widget/src'
import { CountryChangeDto } from '../../../../../../libs/myprofile-country-change-widget/src/lib/interfaces/countryChangeDto'
import { BASE_PATH } from '@myprofile-ui/pidx-utils'

@Injectable({
  providedIn: 'root',
})
export class ProfileService {
  private httpClient = inject(HttpClient)
  private basePath = inject(BASE_PATH)

  public getProfile(country: string, language: string, header?: HttpHeaders): Observable<PublicProfile> {
    const headers = header ? header.set('accept-language', language) : new HttpHeaders({ 'accept-language': language })
    return this.httpClient.get<PublicProfile>(`${this.basePath}?country=${country}`, { headers })
  }

  public updateProfile(country: string, language: string, dto: PublicProfile): Observable<PublicProfile> {
    return this.httpClient.patch<PublicProfile>(`${this.basePath}?country=${country}`, dto, {
      headers: { 'accept-language': language },
    })
  }

  public deleteProfile(country: string, language: string): Observable<void> {
    return this.httpClient.delete<void>(`${this.basePath}?country=${country}`, { headers: { 'accept-language': language } })
  }

  public getProfileCompletionStatus(country: string, language: string): Observable<CompletionStatusDto> {
    return this.httpClient.get<CompletionStatusDto>(`${this.basePath}/status?country=${country}`, {
      headers: { 'accept-language': language },
    })
  }

  public changeCountryForProfile(country: string, language: string, dto: CountryChangeDto): Observable<void> {
    return this.httpClient.patch<void>(`${this.basePath}/country?country=${country}`, dto, {
      headers: { 'accept-language': language },
    })
  }
}

export interface CompletionStatusDto {
  ciamId: string
  completionState: number
  completionDetails: MyprofileProfileCompletionDetails
}

export interface ChangeCountryDto {
  targetCountry: string
  targetLanguage: string
  acceptedTermsAndConditionsVersion: string
  acceptedDataPrivacyVersion?: string
  acceptedDataPrivacyDataCollectionUsageVersion?: string
  acceptedDataPrivacyThirdPartiesVersion?: string
  acceptedDealerConsentVersion?: string
}

export interface PublicProfile {
  ciamId?: string
  porscheId?: string
  acceptedDataPrivacyDate?: Date
  acceptedDataPrivacyVersion?: string
  acceptedDealerConsentDate?: Date
  acceptedDealerConsentVersion?: string
  acceptedTermsAndConditionsDate?: Date
  acceptedTermsAndConditionsVersion?: string
  preferredDealerId?: string
  preferredCommunicationChannel?: string
  dateOfBirth?: string
  dealers?: string[]
  firstName?: string
  identityVerificationState?: string
  lastName?: string
  secondName?: string
  language?: string
  // needed for the payload of patching the profile
  preferredLanguage?: string
  mbbUid?: string
  mbbNames?: string[]
  suffix?: string
  orgId?: string
  middleInitial?: string
  academicTitle?: string
  academicTitleValue?: string
  salutation?: string
  salutationValue?: string
  status?: string
  preferredCountry?: string
  addresses?: PublicAddress[]
  emails?: PublicEmail[]
  mobiles?: PublicMobile[]
  phones?: PublicPhone[]
  vehicles?: PublicVehicle[]
  tmpAddress?: PublicAddress
}

export interface PublicAddress {
  addressId: string
  ciamId: string
  usage: string
  street1: string
  houseNumber: string
  city: string
  postalCode: string
  country: string
  isStandard: boolean
  isExpressBuyAddress: boolean
  isBillingAddress: boolean
  isDeliveryAddress: boolean
  formattedAddress: string
}

export interface PublicEmail {
  emailId: string
  email: string
  usage: string
  isVerified: boolean
  isStandard: boolean
}

export interface PublicMobile {
  mobileId: string
  number: string
  usage: string
  isVerified: boolean
  isStandard: boolean
}

export interface PublicPhone {
  phoneId: string
  number: string
  usage: string
  isStandard: boolean
}

export interface PublicVehicle {
  vin: string
  state: string
  ownershipType: string
  invitedBy: string
  validFrom: any
  validTo?: any
  pcc: boolean
  confirmed: boolean
}
export interface LegalDocument {
  ciamVersion: string
  shopVersion: string
  type: string
  linkPDF?: string
  linkExt?: string
}
